import { Component, OnInit, ViewEncapsulation } from '@angular/core';
import { IWeissmanModalComponent, WeissmanModalService } from '../../Compliance/WeissmanModalService';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { RestrictService, Roles } from '../../Common/Permissions/restrict.service';
import { BusyIndicatorMessageManager } from '../../Busy-Indicator';
import { UntypedFormArray, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import {
    AddTaxYearModalComponent,
    AddTaxYearModalParams
} from '../../Tax-Rate/Tax-Authority/add.tax.year.modal.component';
import { AssessmentComponentTypeEnum, Constants } from '../../constants.new';
import { AssessmentClassService } from '../../Assessment-Class/assessmentClass.service';
import { CapRuleRepository } from '../../Compliance/Repositories/capRule.repository';
import { lastValueFrom } from 'rxjs';
import { MessageModalService } from '../../UI-Lib/Message-Box/messageModal.service';
import { HelpService } from '../../UI-Lib/Help-Tooltip';
import { CAP_RULE_HELP } from '../capRule.help';
import { ToastrService } from 'ngx-toastr';

export interface ICapRuleDetailsParams {
    capRule: Core.CapRuleModel;
}

export interface ICapRuleDetailsResult {
    capRule: Core.CapRuleModel;
    hasChanges: boolean;
    isDeleted: boolean;
}

@Component({
    selector: 'cap-rule-details',
    templateUrl: './capRuleDetails.component.html',
    styleUrls: ['./capRuleDetails.component.scss'],
    encapsulation: ViewEncapsulation.None,
    standalone: false
})
export class CapRuleDetailsComponent implements OnInit, IWeissmanModalComponent<ICapRuleDetailsParams, ICapRuleDetailsResult> {

    constructor(
        _restrictService: RestrictService,
        private readonly bsModalRef: BsModalRef,
        private readonly _weissmanModalService: WeissmanModalService,
        private readonly _formBuilder: UntypedFormBuilder,
        private readonly _constants: Constants,
        private readonly _asssessmentClassService: AssessmentClassService,
        private readonly _capRuleRepository: CapRuleRepository,
        private readonly _messageModalService: MessageModalService,
        private readonly _helpService: HelpService,
        private readonly _toastr: ToastrService) {
        this.canEdit = _restrictService.isInRoles([Roles.JURISDICTIONSETUPS])
    }

    params: ICapRuleDetailsParams;
    result: ICapRuleDetailsResult;
    isLoading: boolean = false  ;
    canEdit: boolean = false;
    busyIndicatorMessageManager: BusyIndicatorMessageManager<string> = new BusyIndicatorMessageManager<string>();
    capRuleForm: UntypedFormGroup;
    capRule: Core.CapRuleModel;
    editMode: boolean = false;
    saving: boolean = false;
    propertyTypes: any[];
    assessmentComponents: Weissman.Model.Assessments.AssessmentComponent[];
    predefinedRules: Core.CapRulePredefinedModel[];
    canBeDeleted: boolean = false;

    get fAnnualPercentIncreases() {
        return (this.capRuleForm.get('annualPercentIncreases') as UntypedFormArray);
    }

    async ngOnInit(): Promise<void> {
        this._helpService.setContent(CAP_RULE_HELP);

        this.propertyTypes = Object.entries(this._constants.PropertyTypeNames).map(([key, value]) => {
            return {
                name: value,
                propertyTypeId: Number(key)
            };
        });

        const messageId = 'initializing';
        this.busyIndicatorMessageManager.add('Initializing', messageId);

        try {
            const canBeDeletedPromise = this.params.capRule && this.canEdit
                ? lastValueFrom(this._capRuleRepository.canBeDeleted(this.params.capRule.capRuleId))
                : Promise.resolve(false);

            const [assessmentComponents, predefinedRules, canBeDeleted] = await Promise.all([
                this._asssessmentClassService.getAssessmentComponents(),
                lastValueFrom(this._capRuleRepository.getPredefinedCapRules()),
                canBeDeletedPromise
            ]);

            this.assessmentComponents = assessmentComponents
                .filter(x => x.assessmentComponentTypeID === AssessmentComponentTypeEnum.Cap)
                .sort((x, y) => x.sequence - y.sequence);
            this.predefinedRules = predefinedRules.sort((x, y) => x.displayOrder - y.displayOrder);
            this.canBeDeleted = canBeDeleted;
        } finally {
            this.busyIndicatorMessageManager.remove(messageId);
        }

        this.capRuleForm = this._formBuilder.group({
            stateId: [null, Validators.required],
            propertyTypeId: [null, Validators.required],
            assessmentComponentId: [null, Validators.required],
            taxYearBegin: [null],
            taxYearEnd: [null],
            capRulePredefinedId: [1],
            capValueRange: [null, Validators.max(1000000000)],
            mayCapExceedAV: [false, Validators.required],
            defaultAnnualPercentIncrease: [null, [Validators.min(0), Validators.max(100)]],
            annualPercentIncreases: this._formBuilder.array([])
        });

        this.capRule = this.params.capRule;

        if (this.capRule) {
            this.capRule = structuredClone(this.capRule);
            console.log(this.capRule)
            this.capRule.annualPercentIncreases = this.capRule
                .annualPercentIncreases.filter(x => x.capRuleAnnualPercentIncreaseId !== 0);
            this._updateForm(true);
        }

        this.editMode = !this.params.capRule;
    }

    goToEditMode() {
        this.editMode = true;
    }

    get canSave(): boolean {
        return this.capRuleForm.dirty && this.capRuleForm.valid;
    }

    async save(): Promise<void> {
        const model = this.capRuleForm.getRawValue() as Core.CapRuleModel;

        model.defaultAnnualPercentIncrease = model.defaultAnnualPercentIncrease / 100;
        model.annualPercentIncreases.forEach(x => x.value = x.value / 100);

        const messageId = 'saving';
        this.busyIndicatorMessageManager.add('Saving', messageId);

        try {
            let capRuleSaved: Core.CapRuleModel;
            if (this.capRule){
                model.capRuleId = this.capRule.capRuleId;
                capRuleSaved = await lastValueFrom(this._capRuleRepository.update(model));
            } else {
                capRuleSaved = await lastValueFrom(this._capRuleRepository.create(model));
            }
            this.result = {
                capRule: capRuleSaved,
                hasChanges: true,
                isDeleted: false
            };
            this.bsModalRef.hide();
        } finally {
            this.busyIndicatorMessageManager.remove(messageId);
        }
    }

    async cancel(): Promise<void> {
        if (this.capRule) {
            this._updateForm(true);
            this.editMode = false;
        } else {
            this.bsModalRef.hide();
        }
    }

    async close(showWarning: boolean): Promise<void> {
        if (this.capRuleForm.dirty && showWarning) {
            try {
                await this._messageModalService.confirm('Editing is in progress. Are you sure you want to leave?');
            } catch (e) {
                return;
            }
        }

        this.bsModalRef.hide();
    }

    async launchAddTaxYearModal(): Promise<void> {
        const params: AddTaxYearModalParams = {
            yearsAlreadyAdded: this.fAnnualPercentIncreases.value.map(x => x.year)
        };

        const year = await this._weissmanModalService.showAsync(AddTaxYearModalComponent, params, 'modal-md');

        if (!year) {
            return;
        }

        const rate = this.capRuleForm.get('defaultAnnualPercentIncrease').value.toFixed(6);

        let items = this.fAnnualPercentIncreases.value;
        items.push({year: year, value: rate});
        this._populatePercentIncreasesGrid(items);
        this.capRuleForm.markAsDirty();
    }

    public removeIncrease(year: number) {
        let items = this.fAnnualPercentIncreases.value;
        const yearIndex = items.findIndex(x => x.year === year);
        items.splice(yearIndex, 1);
        this.fAnnualPercentIncreases.clear();
        items.forEach(x => this.fAnnualPercentIncreases.push(this._createYearControl(x.year, x.value)));
        this.capRuleForm.markAsDirty();
    }

    public get deleteHelp(): string {
        return this.canBeDeleted
            ? 'cap-rule-details.delete'
            : 'cap-rule-details.delete-disabled';
    }

    public async delete(): Promise<void> {
        try {
            await this._messageModalService.confirm(
                `Are you sure you want to delete?`,
                'Confirm Delete'
            );
        } catch (e) {
            return Promise.resolve();
        }

        const busyMsgId = 'deleting';
        this.busyIndicatorMessageManager.add('Deleting template', busyMsgId);

        try {
            await lastValueFrom(this._capRuleRepository.delete(this.capRule.capRuleId));

            this.result = {
                capRule: null,
                hasChanges: false,
                isDeleted: true
            };

            await this.close(false);
        } finally {
            this.busyIndicatorMessageManager.remove(busyMsgId);
        }
    }

    public async recalculateCapComponents(): Promise<void> {
        await lastValueFrom(this._capRuleRepository.recalculateCapComponents(this.capRule.capRuleId));
        this._toastr.success('A new LRP has been submitted.')
    }

    private _createYearControl(year: number, value: number) {
        return this._formBuilder.group({
            year: [year, [Validators.required, Validators.min(1950), Validators.max(2050)]],
            value: [value, [Validators.required, Validators.min(0.000001), Validators.max(100)]]
        });
    }

    private _populatePercentIncreasesGrid(items, convertDecimalToPercent: boolean = false) {
        items.sort((x, y) => y.year - x.year);
        this.fAnnualPercentIncreases.clear();
        items.forEach(x => this.fAnnualPercentIncreases.push(this._createYearControl(x.year, convertDecimalToPercent ? (x.value * 100).toFixed(6) : x.value)));
    }

    private _updateForm(convertDecimalToPercent: boolean) {
        this.capRuleForm.patchValue(this.capRule);
        this._populatePercentIncreasesGrid(this.capRule.annualPercentIncreases, convertDecimalToPercent);

        if (convertDecimalToPercent) {
            this.capRuleForm.get('defaultAnnualPercentIncrease').patchValue(this.capRule.defaultAnnualPercentIncrease * 100);
        }
        this.capRuleForm.markAsPristine();
    }
}
